import { ScriptHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';

import { isBrowser } from '../browser';

export type loadScriptStateType = 'idle' | 'pending' | 'success' | 'error';

export type ScriptOptions = ScriptHTMLAttributes<HTMLScriptElement> & {
  removeOnUnmount?: boolean;
  disabled?: boolean;
};

export async function importGlobalScript(
  url: string,
  setLoadScriptState: (state: loadScriptStateType) => void,
  options?: ScriptOptions,
) {
  if (!isBrowser()) {
    return;
  }

  const alreadyLoaded = document.querySelector(`script[src="${url}"]`) != null;

  return new Promise<void>((resolve) => {
    if (alreadyLoaded) {
      resolve();

      return;
    }

    const script = document.createElement('script');

    if (options != null) {
      Object.entries(options).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
    }

    script.onload = () => {
      setLoadScriptState('success');
      resolve();
    };

    script.onerror = () => {
      document.body.removeChild(script);
      setLoadScriptState('error');

      // We don't want to reject to avoid tracking expected network errors in this case.
      resolve();
    };

    script.src = url;

    document.body.appendChild(script);
  });
}

export function removeGlobalScript(url: string) {
  if (!isBrowser()) {
    return;
  }

  const element = document.querySelector(`script[src="${url}"]`);
  if (element == null) return;

  if (element != null) {
    document.body.removeChild(element);
  }
}

export function useScript(src: string, options: ScriptOptions = {}): loadScriptStateType {
  const { removeOnUnmount, disabled, ...rest } = options;
  const hasExecutedRef = useRef(false);

  const cancel = useCallback(() => {
    return removeGlobalScript(src);
  }, [src]);

  const [loadScriptState, setLoadScriptState] = useState<loadScriptStateType>('idle');

  useEffect(() => {
    if (hasExecutedRef.current === false && !disabled) {
      setLoadScriptState('pending');
      async function loadScript() {
        try {
          await importGlobalScript(src, setLoadScriptState, rest);
          setLoadScriptState('success');
        } catch (e) {
          setLoadScriptState('error');
        }
      }
      loadScript();

      if (removeOnUnmount) {
        return cancel;
      }
    }

    hasExecutedRef.current = true;
  }, [src, cancel, options]);

  return loadScriptState;
}
